import { roundCurrency, calculatePercent } from '@sporkbytes/math-utils';
import Money from '@sporkbytes/money';

/**
 * @description Given a menu item, return the total amount the item will cost for the included quantity before discounts are applied.
 * @param {object} item - The menu item whose total amount is being calculated.
 * @param {string} amountType - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`.
 * @return {number} The currency-rounded total amount for an item for the included quantity before discounts are applied.
 */
function itemTotalBeforeDiscount(item, amountType) {
	if (item != null && item.quantity > 0 && item[amountType]) {
		return roundCurrency(item[amountType] * item.quantity);
	}

	return 0;
}

/**
 * @description Given a menu item, return the amount discounted for the item for the included quantity.  **NOTE** we do not round this as a currency due to how Xero does rounding.  For more information, see [this Asana task]{@link https://app.asana.com/0/525815450072863/610544412664470}.
 * @param {object} item - The menu item whose discount amount is being calculated.
 * @param {string} [amountType = 'costToClient'] - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`, but `paidToPartner` will always return 0 since discounts are not applicable.
 * @return {number} The currency-rounded discount amount for a single item.
 */
function itemTotalDiscount(item, amountType = 'costToClient') {
	if (
		amountType === 'costToClient' &&
		item != null &&
		!!item.discount &&
		item[amountType]
	) {
		// don't round this to the nearest penny with roundCurrency; Xero rounds after the discount is applied (https://app.asana.com/0/525815450072863/610544412664470)
		return calculatePercent(
			itemTotalBeforeDiscount(item, amountType),
			item.discount
		);
	}

	return 0;
}

/**
 * @description Given a menu item, return the total amount the item will cost for the included quantity after discounts are applied.
 * @param {object} item - The menu item whose total amount is being calculated.
 * @param {string} amountType - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`.
 * @return {number} The currency-rounded total amount for an item for the included quantity after any discounts are applied.
 */
function itemTotalAfterDiscount(item, amountType) {
	if (item != null && item[amountType]) {
		const beforeDiscount = new Money(
			itemTotalBeforeDiscount(item, amountType)
		);
		const discount = itemTotalDiscount(item, amountType);

		return +beforeDiscount.subtract(discount);
	}

	return 0;
}

/**
 * @description Given a menu item, return the amount a single item will cost after discounts are applied.
 * @param {object} item - The menu item whose amount is being calculated.
 * @param {string} amountType - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`.
 * @return {number} The currency-rounded amount for a single item after any discounts are applied.
 */
function itemAmountAfterDiscount(item, amountType) {
	if (item != null && item[amountType]) {
		return roundCurrency(
			itemTotalAfterDiscount(item, amountType) / item.quantity
		);
	}

	return 0;
}

/**
 * @description Given a menu item, return the amount discounted for a single item.
 * @param {object} item - The menu item whose discount amount is being calculated.
 * @param {string} [amountType = 'costToClient'] - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`, but `paidToPartner` will always return 0 since discounts are not applicable.
 * @return {number} The currency-rounded discount amount for a single item.
 */
function itemDiscount(item, amountType = 'costToClient') {
	if (
		amountType === 'costToClient' &&
		item != null &&
		!!item.discount &&
		item[amountType]
	) {
		const amount = new Money(item[amountType]);
		const amountAfterDiscount = itemAmountAfterDiscount(item, amountType);

		return +amount.subtract(amountAfterDiscount);
	}

	return 0;
}

/**
 * @description Given a menu item, return the actual amount discounted for the item for the included quantity.  **NOTE** this is rounded as a currency, but is **not necessarily equal to** `itemTotalDiscount` with currency-rounding applied.
 * @param {object} item - The menu item whose discount amount is being calculated.
 * @param {string} [amountType = 'costToClient'] - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`, but `paidToPartner` will always return 0 since discounts are not applicable.
 * @return {number} The currency-rounded discount amount for a single item.
 */
function itemTotalDiscountActual(
	item,
	amountType = 'costToClient'
) {
	if (
		amountType === 'costToClient' &&
		item != null &&
		!!item.discount &&
		item[amountType]
	) {
		const totalBeforeDiscount = new Money(
			itemTotalBeforeDiscount(item, amountType)
		);
		const totalAfterDiscount = itemTotalAfterDiscount(item, amountType);

		return +totalBeforeDiscount.subtract(totalAfterDiscount);
	}

	return 0;
}

export { itemAmountAfterDiscount, itemDiscount, itemTotalAfterDiscount, itemTotalBeforeDiscount, itemTotalDiscount, itemTotalDiscountActual };
